// SDP parsing helpers — narrow the surface to just what the bootstrap
// needs:
//
//   * extract the DTLS fingerprint line (`a=fingerprint:…`) so it can
//     be verified against the peer_id before handing the SDP to
//     `RTCPeerConnection.setRemoteDescription`.
//
// Written as a string parser rather than a full SDP library; the SDP
// coming from the box has a small, predictable shape. The function
// handles multiple fingerprint lines (only the first is returned —
// WebRTC uses one cert per session) and normalises hex representation
// (uppercase / colon separators are stripped).

export interface SdpFingerprint {
  /** Algorithm name as it appears in the SDP (e.g. "sha-256"). */
  algorithm: string;
  /** Raw fingerprint bytes (`:`-delimited hex decoded). */
  value: Uint8Array;
}

/**
 * Extract the first `a=fingerprint:<alg> <hex:hex:…>` line from an
 * SDP string. Returns `null` if no fingerprint line is present.
 *
 * Throws on a malformed fingerprint (non-hex, odd nibble count).
 */
export function extractFingerprint(sdp: string): SdpFingerprint | null {
  // SDP uses CRLF; we tolerate bare LF too.
  for (const rawLine of sdp.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line.toLowerCase().startsWith("a=fingerprint:")) continue;
    const body = line.slice("a=fingerprint:".length).trim();
    const spaceIdx = body.indexOf(" ");
    if (spaceIdx < 0) {
      throw new Error(`malformed SDP fingerprint line: ${JSON.stringify(line)}`);
    }
    const algorithm = body.slice(0, spaceIdx).toLowerCase();
    const hexColon = body.slice(spaceIdx + 1).trim();
    const hex = hexColon.replace(/:/g, "").toLowerCase();
    if (hex.length % 2 !== 0) {
      throw new Error(`malformed SDP fingerprint: odd hex length: ${hex}`);
    }
    const value = new Uint8Array(hex.length / 2);
    for (let i = 0; i < value.length; i++) {
      const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
      if (!Number.isFinite(byte)) {
        throw new Error(`malformed SDP fingerprint: bad hex byte at ${i}`);
      }
      value[i] = byte;
    }
    return { algorithm, value };
  }
  return null;
}

/**
 * Map SDP fingerprint algorithm names to the on-the-wire `fp_alg`
 * byte used in the signed payload. Only SHA-256 is specified in
 * ; other algorithms are reserved.
 */
export function fpAlgByte(algorithm: string): number {
  switch (algorithm.toLowerCase()) {
    case "sha-256":
      return 0x01;
    default:
      throw new Error(`unsupported SDP fingerprint algorithm: ${algorithm}`);
  }
}
